'use client';

import { c as _c } from "react/compiler-runtime";
import type { EditorView } from '@codemirror/view';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ClientUploadedFileData } from 'uploadthing/types';
import { useMediaQuery } from 'usehooks-ts';
import { SCROLL_AREA, type TreeData, useSyncScroll } from '../../_hooks/use-sync-scroll';
import { Preview } from '../Preview';
import { EditorLoader, SingleViewEditor, SplitViewEditor } from './_components';
import { useContentFormatter } from './_hooks/useContentFormatter';
import { useEditorLocalStorage } from './_hooks/useEditorLocalStorage';
import { AttachmentsManager, type DbAttachment, mapAttachmentsToFiles } from './AttachmentsManager';
import { Editor } from './Editor';
export type MarkdownContent = {
  markdown: string;
  attachments?: DbAttachment[];
};
export type MarkdownEditorProps = {
  value?: MarkdownContent;
  singleView?: boolean;
  height?: number;
  disabled?: boolean;
  markdownOnly?: boolean;
  onChange?: (value: {
    markdown: string;
    attachments: DbAttachment[];
  }) => void;
};
export const MarkdownEditor: React.FC<MarkdownEditorProps> = t0 => {
  const $ = _c(92);
  const {
    value,
    singleView,
    height,
    disabled: t1,
    markdownOnly: t2,
    onChange
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const markdownOnly = t2 === undefined ? false : t2;
  const [initialValue] = useEditorLocalStorage(value);
  const {
    format,
    isFormatting
  } = useContentFormatter();
  const content = value?.markdown ?? initialValue.markdown;
  value?.attachments;
  const t3 = value?.attachments ?? initialValue.attachments;
  let t4;
  if ($[0] !== t3) {
    t4 = mapAttachmentsToFiles(t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const files = t4;
  const [originalContent, setOriginalContent] = useState(content);
  const [showDiff, setShowDiff] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [currentScrollArea, setCurrentScrollArea] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const previewRef = useRef(null);
  const editorViewRef = useRef(null);
  const treeDataRef = useRef(null);
  const editorContainerRef = useRef(null);
  const diffContentRef = useRef(null);
  let t5;
  if ($[2] !== currentScrollArea) {
    t5 = {
      previewRef,
      editorViewRef,
      treeDataRef,
      currentScrollArea,
      editorContainerRef
    };
    $[2] = currentScrollArea;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    handleEditorScroll,
    handlePreviewScroll,
    computePosition,
    getCurrentEditorScrollPosition,
    getCurrentPreviewScrollPosition,
    syncToPreview,
    syncToEditor
  } = useSyncScroll(t5);
  let t6;
  if ($[4] !== files || $[5] !== isFormatting || $[6] !== onChange || $[7] !== showDiff) {
    t6 = value_0 => {
      if (showDiff) {
        diffContentRef.current = value_0;
        return;
      }
      if (!isFormatting) {
        onChange?.({
          markdown: value_0,
          attachments: files.filter(_temp).map(_temp2)
        });
      }
    };
    $[4] = files;
    $[5] = isFormatting;
    $[6] = onChange;
    $[7] = showDiff;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleContentChange = t6;
  let t7;
  if ($[9] !== content || $[10] !== onChange) {
    t7 = newFiles => {
      onChange?.({
        markdown: content,
        attachments: newFiles.map(_temp3)
      });
    };
    $[9] = content;
    $[10] = onChange;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleFilesChange = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = view => {
      editorViewRef.current = view;
      setEditorReady(true);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleCreateEditor = t8;
  let t9;
  if ($[13] !== content || $[14] !== format || $[15] !== onChange || $[16] !== value?.attachments) {
    t9 = async () => {
      const formatted = await format(content);
      if (!formatted) {
        return;
      }
      setOriginalContent(content);
      onChange?.({
        markdown: formatted,
        attachments: value?.attachments ?? []
      });
      setShowDiff(true);
    };
    $[13] = content;
    $[14] = format;
    $[15] = onChange;
    $[16] = value?.attachments;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  value?.attachments;
  const handleFormatContent = t9;
  let t10;
  if ($[18] !== content || $[19] !== onChange || $[20] !== value?.attachments) {
    t10 = () => {
      const finalContent = diffContentRef.current ?? content;
      diffContentRef.current = null;
      setShowDiff(false);
      setOriginalContent(finalContent);
      onChange?.({
        markdown: finalContent,
        attachments: value?.attachments ?? []
      });
    };
    $[18] = content;
    $[19] = onChange;
    $[20] = value?.attachments;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  value?.attachments;
  const handleDiffComplete = t10;
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const isSingleView = singleView ?? isMobile;
  let t11;
  if ($[22] !== computePosition || $[23] !== getCurrentEditorScrollPosition || $[24] !== getCurrentPreviewScrollPosition || $[25] !== showPreview || $[26] !== syncToEditor || $[27] !== syncToPreview) {
    t11 = () => {
      if (showPreview) {
        computePosition();
        const previewScrollPos = getCurrentPreviewScrollPosition();
        setShowPreview(false);
        requestAnimationFrame(() => syncToEditor(previewScrollPos));
      } else {
        const editorScrollPos = getCurrentEditorScrollPosition();
        setShowPreview(true);
        requestAnimationFrame(() => syncToPreview(editorScrollPos));
      }
    };
    $[22] = computePosition;
    $[23] = getCurrentEditorScrollPosition;
    $[24] = getCurrentPreviewScrollPosition;
    $[25] = showPreview;
    $[26] = syncToEditor;
    $[27] = syncToPreview;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleToggleView = t11;
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setCurrentScrollArea(SCROLL_AREA.EDITOR);
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = !isFormatting && !disabled;
  let t14;
  if ($[30] !== files || $[31] !== handleFilesChange) {
    t14 = attachment => handleFilesChange([...files, attachment]);
    $[30] = files;
    $[31] = handleFilesChange;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== handleFormatContent || $[34] !== singleView) {
    t15 = {
      singleView,
      onFormat: handleFormatContent
    };
    $[33] = handleFormatContent;
    $[34] = singleView;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== content || $[37] !== handleContentChange || $[38] !== handleDiffComplete || $[39] !== handleEditorScroll || $[40] !== originalContent || $[41] !== showDiff || $[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <Editor ref={editorContainerRef} content={content} setContent={handleContentChange} originalContent={originalContent} onCreateEditor={handleCreateEditor} onScroll={handleEditorScroll} onMouseEnter={t12} showDiff={showDiff} onDiffComplete={handleDiffComplete} isEditable={t13} onAddAttachment={t14} passDownProps={t15} />;
    $[36] = content;
    $[37] = handleContentChange;
    $[38] = handleDiffComplete;
    $[39] = handleEditorScroll;
    $[40] = originalContent;
    $[41] = showDiff;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const EditorSection = t16;
  let t17;
  if ($[46] !== content) {
    t17 = {
      markdown: content
    };
    $[46] = content;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setCurrentScrollArea(SCROLL_AREA.PREVIEW);
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setPreviewReady(true);
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== handlePreviewScroll || $[51] !== t17) {
    t20 = <Preview ref={previewRef} content={t17} onScroll={handlePreviewScroll} onMouseEnter={t18} treeDataRef={treeDataRef} onReady={t19} className="px-6" />;
    $[50] = handlePreviewScroll;
    $[51] = t17;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const PreviewSection = t20;
  if (isSingleView) {
    let t21;
    if ($[53] !== editorReady || $[54] !== height) {
      t21 = !editorReady && <EditorLoader height={height} />;
      $[53] = editorReady;
      $[54] = height;
      $[55] = t21;
    } else {
      t21 = $[55];
    }
    let t22;
    if ($[56] !== disabled || $[57] !== files || $[58] !== handleFilesChange || $[59] !== markdownOnly || $[60] !== showPreview) {
      t22 = !markdownOnly && <AttachmentsManager files={files} setFiles={handleFilesChange} singleView={true} preview={showPreview} disabled={disabled} />;
      $[56] = disabled;
      $[57] = files;
      $[58] = handleFilesChange;
      $[59] = markdownOnly;
      $[60] = showPreview;
      $[61] = t22;
    } else {
      t22 = $[61];
    }
    let t23;
    if ($[62] !== EditorSection || $[63] !== PreviewSection || $[64] !== editorReady || $[65] !== handleToggleView || $[66] !== height || $[67] !== showPreview || $[68] !== t22) {
      t23 = <SingleViewEditor editorReady={editorReady} showPreview={showPreview} handleToggleView={handleToggleView} EditorSection={EditorSection} PreviewSection={PreviewSection} height={height}>{t22}</SingleViewEditor>;
      $[62] = EditorSection;
      $[63] = PreviewSection;
      $[64] = editorReady;
      $[65] = handleToggleView;
      $[66] = height;
      $[67] = showPreview;
      $[68] = t22;
      $[69] = t23;
    } else {
      t23 = $[69];
    }
    let t24;
    if ($[70] !== t21 || $[71] !== t23) {
      t24 = <>{t21}{t23}</>;
      $[70] = t21;
      $[71] = t23;
      $[72] = t24;
    } else {
      t24 = $[72];
    }
    return t24;
  }
  let t21;
  if ($[73] !== editorReady || $[74] !== height || $[75] !== previewReady) {
    t21 = !editorReady && !previewReady && <EditorLoader height={height} />;
    $[73] = editorReady;
    $[74] = height;
    $[75] = previewReady;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== disabled || $[78] !== files || $[79] !== handleFilesChange || $[80] !== markdownOnly) {
    t22 = !markdownOnly && <AttachmentsManager files={files} setFiles={handleFilesChange} disabled={disabled} />;
    $[77] = disabled;
    $[78] = files;
    $[79] = handleFilesChange;
    $[80] = markdownOnly;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] !== EditorSection || $[83] !== PreviewSection || $[84] !== editorReady || $[85] !== height || $[86] !== previewReady || $[87] !== t22) {
    t23 = <SplitViewEditor editorReady={editorReady} previewReady={previewReady} EditorSection={EditorSection} PreviewSection={PreviewSection} height={height}>{t22}</SplitViewEditor>;
    $[82] = EditorSection;
    $[83] = PreviewSection;
    $[84] = editorReady;
    $[85] = height;
    $[86] = previewReady;
    $[87] = t22;
    $[88] = t23;
  } else {
    t23 = $[88];
  }
  let t24;
  if ($[89] !== t21 || $[90] !== t23) {
    t24 = <>{t21}{t23}</>;
    $[89] = t21;
    $[90] = t23;
    $[91] = t24;
  } else {
    t24 = $[91];
  }
  return t24;
};
function _temp(file) {
  return file !== undefined;
}
function _temp2(file_0) {
  return {
    name: file_0.name,
    url: file_0.ufsUrl
  };
}
function _temp3(file_1) {
  return {
    name: file_1.name,
    url: file_1.ufsUrl
  };
}